import { createI18n } from 'vue-i18n'
import { IS_JP_ONLY_BUILD } from '@/common/const/build-locale'
import { loadMainLocaleMessages } from '@/locales/load-main-messages'
import baselineRepairNZhCN from '@/locales/baseline-repair-items/n.zh-CN.json'
import baselineRepairNEnUS from '@/locales/baseline-repair-items/n.en-US.json'
import baselineRepairNJaJP from '@/locales/baseline-repair-items/n.ja-JP.json'
import baselineRepairNZhTW from '@/locales/baseline-repair-items/n.zh-TW.json'
import baselineRepairDescZhCN from '@/locales/baseline-repair-items/desc.zh-CN.json'
import baselineRepairDescEnUS from '@/locales/baseline-repair-items/desc.en-US.json'
import baselineRepairDescJaJP from '@/locales/baseline-repair-items/desc.ja-JP.json'
import baselineRepairDescZhTW from '@/locales/baseline-repair-items/desc.zh-TW.json'
import virusVZhCN from '@/locales/virus-items/v.zh-CN.json'
import virusVZhTW from '@/locales/virus-items/v.zh-TW.json'
import virusVEnUS from '@/locales/virus-items/v.en-US.json'
import virusVJaJP from '@/locales/virus-items/v.ja-JP.json'
import adminOperationActionsZhCN from '@/locales/notice-manage/admin-operation-actions.zh-CN.json'
import adminOperationActionsZhTW from '@/locales/notice-manage/admin-operation-actions.zh-TW.json'
import adminOperationActionsEnUS from '@/locales/notice-manage/admin-operation-actions.en-US.json'
import adminOperationActionsJaJP from '@/locales/notice-manage/admin-operation-actions.ja-JP.json'
import warningLogActionsZhCN from '@/locales/notice-manage/warning-log-actions.zh-CN.json'
import warningLogActionsZhTW from '@/locales/notice-manage/warning-log-actions.zh-TW.json'
import warningLogActionsEnUS from '@/locales/notice-manage/warning-log-actions.en-US.json'
import warningLogActionsJaJP from '@/locales/notice-manage/warning-log-actions.ja-JP.json'
import { subscribePriceTitleMessages } from '@/views/dashboard/service/my-subscribe/subscribe-price-title.messages'
import { subscribeServiceGroupClassMessages } from '@/views/dashboard/service/my-subscribe/subscribe-service-group.messages'
import { messagePushTaskTypeMessages } from '@/views/dashboard/notice-manage/message-push/task-type.messages'
import portRiskPortsZhCN from '@/locales/port-manage/risk-ports.zh-CN.json'
import portRiskPortsZhTW from '@/locales/port-manage/risk-ports.zh-TW.json'
import portRiskPortsEnUS from '@/locales/port-manage/risk-ports.en-US.json'
import portRiskPortsJaJP from '@/locales/port-manage/risk-ports.ja-JP.json'
import softGroupCatalogZhCN from '@/locales/softmgr/soft-group-catalog.zh-CN.json'
import softGroupCatalogZhTW from '@/locales/softmgr/soft-group-catalog.zh-TW.json'
import softGroupCatalogEnUS from '@/locales/softmgr/soft-group-catalog.en-US.json'
import softGroupCatalogJaJP from '@/locales/softmgr/soft-group-catalog.ja-JP.json'
import securityEventZhCN from '@/locales/modules/security-event/zh-CN.json'
import securityEventZhTW from '@/locales/modules/security-event/zh-TW.json'
import securityEventEnUS from '@/locales/modules/security-event/en-US.json'
import securityEventJaJP from '@/locales/modules/security-event/ja-JP.json'

export type LocaleType = 'zh-CN' | 'zh-TW' | 'en-US' | 'ja-JP'

const zhCN = loadMainLocaleMessages('zh-CN')
const zhTW = loadMainLocaleMessages('zh-TW')
const enUS = loadMainLocaleMessages('en-US')
const jaJP = loadMainLocaleMessages('ja-JP')

/**
 * 由 main 在 root-injector 就绪后注册，避免 i18n → http → root-injector → service-permission → i18n 循环依赖
 * （否则 ServicePermissionService 未初始化即被 `new`）
 */
let httpLangSync: ((locale: LocaleType) => void) | null = null

export function registerHttpLangSync(fn: (locale: LocaleType) => void): void {
  httpLangSync = fn
  fn(i18n.global.locale.value as LocaleType)
}

/** 语言选项，用于下拉展示 */
export const localeOptions: { value: LocaleType; label: string }[] = [
  { value: 'zh-CN', label: '简体中文' },
  { value: 'zh-TW', label: '繁體中文' },
  { value: 'en-US', label: 'English' },
  { value: 'ja-JP', label: '日本語' },
]

/**
 * 日文专版：侧栏与页头「数据安全」下该项在日语包外也展示为「敏感文件发现」语义；
 * 用户切换界面语言时，菜单标题与描述随当前语言展示（与各语言包 JSON 独立，构建时写入）。
 */
const JP_ONLY_SENSITIVE_FILE_SCAN_MENU_PATCHES: Record<LocaleType, { sensitiveFileScan: string }> = {
  'zh-CN': { sensitiveFileScan: '敏感文件发现' },
  'zh-TW': { sensitiveFileScan: '敏感檔案發現' },
  'en-US': { sensitiveFileScan: 'Sensitive file discovery' },
  'ja-JP': { sensitiveFileScan: '機密ファイル検出' },
}

const JP_ONLY_SENSITIVE_FILE_SCAN_MENU_DESCRIBE_PATCHES: Record<LocaleType, { sensitiveFileScan: string }> = {
  'zh-CN': {
    sensitiveFileScan:
      '基于配置的敏感关键词，检测终端上的敏感文件，并支持敏感文件全生命周期追踪。',
  },
  'zh-TW': {
    sensitiveFileScan:
      '依設定的敏感關鍵詞，偵測終端上的敏感檔案，並支援敏感檔案全生命週期追蹤。',
  },
  'en-US': {
    sensitiveFileScan:
      'Detect sensitive files on endpoints using configured sensitive keywords, with full lifecycle tracking.',
  },
  'ja-JP': {
    sensitiveFileScan:
      '設定した機密キーワードに基づいて端末の機密ファイルを検出し、機密ファイルのライフサイクル追跡をサポートします',
  },
}

function applyJpOnlyBuildSensitiveScanCopy<
  T extends Record<LocaleType, Record<string, unknown>>
>(base: T): T {
  if (!IS_JP_ONLY_BUILD) {
    return base
  }
  const out = { ...base }
  for (const loc of ['zh-CN', 'zh-TW', 'en-US', 'ja-JP'] as const) {
    const msg = out[loc]
    out[loc] = {
      ...msg,
      menu: {
        ...(msg.menu as Record<string, unknown>),
        ...JP_ONLY_SENSITIVE_FILE_SCAN_MENU_PATCHES[loc],
      },
      menuDescribe: {
        ...(msg.menuDescribe as Record<string, unknown>),
        ...JP_ONLY_SENSITIVE_FILE_SCAN_MENU_DESCRIBE_PATCHES[loc],
      },
    }
  }
  return out
}

function mergeLocaleBundle (
  main: Record<string, unknown>,
  securityEvent: Record<string, unknown>,
  portRisk: Record<string, unknown>,
  virusKb: Record<string, unknown>,
  baselineN: Record<string, unknown>,
  baselineDesc: Record<string, unknown>,
  adminOp: Record<string, unknown>,
  warnLog: Record<string, unknown>,
  taskType: Record<string, string>,
  priceTitle: Record<string, string>,
  serviceGroupClass: Record<string, string>,
  softGroupCatalog: Record<string, unknown>,
) {
  const noticeManage = main.noticeManage as Record<string, unknown> | undefined
  const mySubscribe = main.mySubscribe as Record<string, unknown> | undefined
  const portManage = main.portManage as Record<string, unknown> | undefined
  return {
    ...main,
    ...securityEvent,
    portManage: {
      ...(portManage || {}),
      ...portRisk,
    },
    virusKbDescriptions: virusKb,
    baselineRepairItems: { n: baselineN, desc: baselineDesc },
    noticeManage: {
      ...(noticeManage || {}),
      adminOperationActions: adminOp,
      warningLogActions: warnLog,
      taskType,
    },
    mySubscribe: {
      ...(mySubscribe || {}),
      priceTitle,
      serviceGroupClass,
    },
    softGroupCatalog,
  }
}

const messages = applyJpOnlyBuildSensitiveScanCopy({
  'zh-CN': mergeLocaleBundle(
    zhCN,
    securityEventZhCN,
    portRiskPortsZhCN,
    virusVZhCN,
    baselineRepairNZhCN,
    baselineRepairDescZhCN,
    adminOperationActionsZhCN,
    warningLogActionsZhCN,
    messagePushTaskTypeMessages['zh-CN'],
    subscribePriceTitleMessages['zh-CN'],
    subscribeServiceGroupClassMessages['zh-CN'],
    softGroupCatalogZhCN,
  ),
  'zh-TW': mergeLocaleBundle(
    zhTW,
    securityEventZhTW,
    portRiskPortsZhTW,
    virusVZhTW,
    baselineRepairNZhTW,
    baselineRepairDescZhTW,
    adminOperationActionsZhTW,
    warningLogActionsZhTW,
    messagePushTaskTypeMessages['zh-TW'],
    subscribePriceTitleMessages['zh-TW'],
    subscribeServiceGroupClassMessages['zh-TW'],
    softGroupCatalogZhTW,
  ),
  'en-US': mergeLocaleBundle(
    enUS,
    securityEventEnUS,
    portRiskPortsEnUS,
    virusVEnUS,
    baselineRepairNEnUS,
    baselineRepairDescEnUS,
    adminOperationActionsEnUS,
    warningLogActionsEnUS,
    messagePushTaskTypeMessages['en-US'],
    subscribePriceTitleMessages['en-US'],
    subscribeServiceGroupClassMessages['en-US'],
    softGroupCatalogEnUS,
  ),
  'ja-JP': mergeLocaleBundle(
    jaJP,
    securityEventJaJP,
    portRiskPortsJaJP,
    virusVJaJP,
    baselineRepairNJaJP,
    baselineRepairDescJaJP,
    adminOperationActionsJaJP,
    warningLogActionsJaJP,
    messagePushTaskTypeMessages['ja-JP'],
    subscribePriceTitleMessages['ja-JP'],
    subscribeServiceGroupClassMessages['ja-JP'],
    softGroupCatalogJaJP,
  ),
})

const defaultLocale: LocaleType = 'zh-CN'

function isStoredLocale (v: string | null): v is LocaleType {
  return v === 'zh-CN' || v === 'zh-TW' || v === 'en-US' || v === 'ja-JP'
}

// 从本地存储或浏览器语言获取初始语言；JP 专版无存储时默认 ja-JP，但仍可读存储以尊重用户切换
function getInitialLocale(): LocaleType {
  try {
    const stored = localStorage.getItem('locale')
    if (isStoredLocale(stored)) {
      return stored
    }
  } catch {
    // ignore
  }
  if (IS_JP_ONLY_BUILD) {
    return 'ja-JP'
  }
  try {
    const lang = navigator.language.toLowerCase()
    if (lang.startsWith('en')) return 'en-US'
    if (lang.startsWith('ja')) return 'ja-JP'
    if (lang === 'zh-tw' || lang.startsWith('zh-hant')) return 'zh-TW'
  } catch {
    // ignore
  }
  return defaultLocale
}

export const i18n = createI18n({
  legacy: false, // 使用 Composition API 模式
  locale: getInitialLocale(),
  fallbackLocale: IS_JP_ONLY_BUILD ? 'ja-JP' : defaultLocale,
  messages,
  globalInjection: true,
})

export function setLocale(locale: LocaleType): void {
  const next = isStoredLocale(locale) ? locale : IS_JP_ONLY_BUILD ? 'ja-JP' : defaultLocale
  i18n.global.locale.value = next
  httpLangSync?.(next)
  try {
    localStorage.setItem('locale', next)
  } catch {
    // ignore
  }
}

export function getLocale(): LocaleType {
  return i18n.global.locale.value as LocaleType
}
